import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Sparkles, Send, Loader2, CheckCircle2, XCircle, ChevronDown, ChevronUp,
  RotateCcw, Zap, Code2, Palette, FileText, ShoppingCart, Calendar,
  Users, Globe, Layout, PlusCircle, Trash2, Edit3,
  ArrowRight, RefreshCw, AlertCircle, Info, Bot, User, Copy, Check,
  Database, Server, Lock, BarChart2, Image, Search, Smartphone, Star,
  ChevronRight, MessageSquare, Layers, Settings, Package, CreditCard,
  Mail, Phone, MapPin, Clock, Tag, Bookmark, TrendingUp, Shield
} from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
  ? process.env.REACT_APP_BACKEND_URL
  : '';

// ─── Quick action categories ───────────────────────────────────────────────────
const QUICK_ACTION_CATEGORIES = [
  {
    label: 'Design',
    icon: Palette,
    color: '#8b5cf6',
    actions: [
      { icon: Palette,    label: 'Change theme colors',     prompt: 'Change the primary color to a deep navy blue and update the accent color to gold. Make the overall design feel more premium.' },
      { icon: Layout,     label: 'Redesign hero section',   prompt: 'Redesign the hero section with a full-width background image, a bold headline, a subtitle, and two CTA buttons side by side.' },
      { icon: Smartphone, label: 'Make mobile responsive',  prompt: 'Make the entire website fully mobile responsive with proper breakpoints, touch-friendly buttons, and a hamburger menu for mobile.' },
      { icon: Star,       label: 'Add testimonials',        prompt: 'Add a testimonials section with 3 customer reviews, star ratings, customer names and photos.' },
    ]
  },
  {
    label: 'Pages',
    icon: FileText,
    color: '#06b6d4',
    actions: [
      { icon: FileText,   label: 'Add About page',          prompt: 'Create a new About Us page with a team section, company story, and our values section with icons.' },
      { icon: Globe,      label: 'Add Blog page',           prompt: 'Create a Blog page with a featured post at the top and a grid of recent articles with thumbnails, titles, dates, and read more links.' },
      { icon: Tag,        label: 'Add Pricing page',        prompt: 'Create a Pricing page with 3 tiers (Basic, Pro, Enterprise), feature lists, and highlighted CTA buttons for each plan.' },
      { icon: Phone,      label: 'Add Contact page',        prompt: 'Create a Contact Us page with a contact form (name, email, phone, message), office address, phone number, and an embedded map placeholder.' },
    ]
  },
  {
    label: 'Features',
    icon: Zap,
    color: '#f59e0b',
    actions: [
      { icon: ShoppingCart, label: 'Add product catalog',   prompt: 'Add a Shop page with a product grid, category filters, and individual product cards with Add to Cart buttons.' },
      { icon: Calendar,   label: 'Add booking system',      prompt: 'Add a Book Appointment page with a booking form that includes date picker, time slots, service selection, name, email, and phone.' },
      { icon: Users,      label: 'Add user login',          prompt: 'Enable visitor login and signup so users can create accounts and log in to access member-only content.' },
      { icon: Mail,       label: 'Add newsletter form',     prompt: 'Add a newsletter signup section with an email input, subscribe button, and a brief value proposition message.' },
    ]
  },
  {
    label: 'Backend',
    icon: Server,
    color: '#10b981',
    actions: [
      { icon: Database,   label: 'Add contact storage',     prompt: 'Generate a backend API to store contact form submissions in a database and send email notifications when new submissions arrive.' },
      { icon: Server,     label: 'Add REST API',            prompt: 'Generate a REST API with CRUD endpoints for managing a custom data collection. Include proper validation and error handling.' },
      { icon: BarChart2,  label: 'Add analytics dashboard', prompt: 'Create an analytics dashboard page showing visitor stats, page views, and conversion metrics with charts.' },
      { icon: Shield,     label: 'Add admin panel',         prompt: 'Create a password-protected admin panel where I can manage website content, view form submissions, and update settings.' },
    ]
  },
  {
    label: 'SEO & Marketing',
    icon: TrendingUp,
    color: '#ef4444',
    actions: [
      { icon: Search,     label: 'Optimize SEO',            prompt: 'Update the SEO title, meta description, and add proper heading structure (H1, H2, H3) throughout the website for better search engine ranking.' },
      { icon: TrendingUp, label: 'Add social proof',        prompt: 'Add a social proof section with client logos, stats (e.g. "10,000+ customers"), and a featured press mention.' },
      { icon: Image,      label: 'Add image gallery',       prompt: 'Add a photo gallery section with a masonry grid layout, lightbox popup on click, and category filter tabs.' },
      { icon: MapPin,     label: 'Add location section',    prompt: 'Add a location/map section with the business address, opening hours, phone number, and an embedded map.' },
    ]
  },
];

// Flat list for backwards compat
const QUICK_ACTIONS = QUICK_ACTION_CATEGORIES.flatMap(c => c.actions);

// ─── Action type icons ────────────────────────────────────────────────────────
const ACTION_ICONS = {
  create_page:              PlusCircle,
  delete_page:              Trash2,
  update_section:           Edit3,
  code_edit:                Code2,
  update_website:           Zap,
  add_form:                 FileText,
  reorder_sections:         Layout,
  delete_section:           Trash2,
  enable_booking:           Calendar,
  enable_product_catalog:   ShoppingCart,
  enable_visitor_login:     Lock,
  generate_backend:         Server,
  edit_backend_file:        Code2,
  update_app_schema:        Globe,
  add_widget:               PlusCircle,
  update_widget:            Edit3,
  remove_widget:            Trash2,
  add_resource:             Database,
  update_resource:          Database,
  remove_resource:          Database,
  set_theme_tokens:         Palette,
  // Phase 5 expanded actions
  update_global_styles:     Palette,
  create_backend_function:  Code2,
  send_email_on_submit:     Mail,
  add_payment_checkout:     CreditCard,
  generate_section_html:    Sparkles,
  create_resource:          Database,
  seed_data:                Database,
  update_resource_policy:   Lock,
  add_nav_link:             Globe,
  remove_nav_link:          Globe,
  add_form_to_page:         FileText,
  default:                  Zap,
};

// ─── Autonomous step card ───────────────────────────────────────────────────
function AutonomousStepCard({ steps, currentStepId, completedSteps, failedSteps }) {
  return (
    <div style={{background:'rgba(31,41,55,0.8)',border:'1px solid rgba(139,92,246,0.3)',borderRadius:'12px',overflow:'hidden'}}>
      <div style={{padding:'10px 14px',borderBottom:'1px solid rgba(75,85,99,0.3)',display:'flex',alignItems:'center',gap:'8px'}}>
        <Zap style={{width:'13px',height:'13px',color:'#8b5cf6'}} />
        <span style={{fontSize:'13px',fontWeight:600,color:'#c4b5fd'}}>Autonomous Plan</span>
        <span style={{fontSize:'11px',color:'#6b7280',marginLeft:'auto'}}>{completedSteps.length}/{steps.length} done</span>
      </div>
      <div style={{padding:'8px 14px',display:'flex',flexDirection:'column',gap:'6px'}}>
        {steps.map((step, i) => {
          const isActive = step.id === currentStepId;
          const isDone = completedSteps.includes(step.id);
          const isFailed = failedSteps.includes(step.id);
          return (
            <div key={step.id || i} style={{display:'flex',alignItems:'flex-start',gap:'8px',fontSize:'12px',opacity: isDone || isActive ? 1 : 0.5}}>
              <div style={{width:'16px',height:'16px',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:'1px',
                background: isDone ? 'rgba(16,185,129,0.2)' : isFailed ? 'rgba(239,68,68,0.2)' : isActive ? 'rgba(139,92,246,0.2)' : 'rgba(75,85,99,0.2)',
                border: `1px solid ${isDone ? '#10b981' : isFailed ? '#ef4444' : isActive ? '#8b5cf6' : '#374151'}`
              }}>
                {isDone ? <CheckCircle2 style={{width:'9px',height:'9px',color:'#10b981'}} />
                  : isFailed ? <XCircle style={{width:'9px',height:'9px',color:'#ef4444'}} />
                  : isActive ? <Loader2 style={{width:'9px',height:'9px',color:'#8b5cf6',animation:'spin 1s linear infinite'}} />
                  : <span style={{width:'5px',height:'5px',borderRadius:'50%',background:'#4b5563',display:'block'}} />}
              </div>
              <div style={{flex:1}}>
                <div style={{color: isDone ? '#34d399' : isFailed ? '#f87171' : isActive ? '#c4b5fd' : '#9ca3af'}}>{step.title || step.type}</div>
                {step.rationale && <div style={{fontSize:'11px',color:'#4b5563',marginTop:'2px'}}>{step.rationale}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Capability badges ────────────────────────────────────────────────────────
const CAPABILITIES = [
  { icon: Palette,    label: 'Design & UI' },
  { icon: FileText,   label: 'Pages' },
  { icon: Code2,      label: 'Code' },
  { icon: Server,     label: 'Backend' },
  { icon: Database,   label: 'Database' },
  { icon: ShoppingCart, label: 'E-Commerce' },
  { icon: Calendar,   label: 'Booking' },
  { icon: Search,     label: 'SEO' },
  { icon: CreditCard, label: 'Payments' },
  { icon: Mail,       label: 'Email' },
  { icon: Sparkles,   label: 'Autonomous' },
];

// ─── Message bubble ───────────────────────────────────────────────────────────
function MessageBubble({ message }) {
  const [copied, setCopied] = useState(false);
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isSystem) {
    return (
      <div style={{display:'flex',alignItems:'flex-start',gap:'8px',padding:'8px 12px',background:'rgba(59,130,246,0.1)',border:'1px solid rgba(59,130,246,0.2)',borderRadius:'8px',fontSize:'12px',color:'#93c5fd'}}>
        <Info style={{width:'14px',height:'14px',marginTop:'2px',flexShrink:0}} />
        <span>{message.content}</span>
      </div>
    );
  }

  return (
    <div style={{display:'flex',alignItems:'flex-start',gap:'10px',flexDirection: isUser ? 'row-reverse' : 'row'}}>
      {/* Avatar */}
      <div style={{
        width:'28px',height:'28px',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,
        background: isUser ? '#7c3aed' : 'linear-gradient(135deg, #7c3aed, #9333ea)'
      }}>
        {isUser ? <User style={{width:'13px',height:'13px',color:'white'}} /> : <Bot style={{width:'13px',height:'13px',color:'white'}} />}
      </div>
      {/* Bubble */}
      <div style={{
        position:'relative',maxWidth:'85%',borderRadius: isUser ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
        padding:'10px 14px',fontSize:'13px',lineHeight:'1.5',
        background: isUser ? '#7c3aed' : '#1f2937',
        color: isUser ? 'white' : '#f3f4f6',
        border: isUser ? 'none' : '1px solid rgba(75,85,99,0.5)',
        whiteSpace:'pre-wrap',wordBreak:'break-word'
      }} className="group">
        {message.content}
        {!isUser && (
          <button
            onClick={handleCopy}
            style={{position:'absolute',top:'6px',right:'6px',padding:'4px',borderRadius:'4px',background:'transparent',border:'none',cursor:'pointer',opacity:0,transition:'opacity 0.2s',color:'#9ca3af'}}
            className="copy-btn"
          >
            {copied ? <Check style={{width:'11px',height:'11px',color:'#34d399'}} /> : <Copy style={{width:'11px',height:'11px'}} />}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Thinking indicator ───────────────────────────────────────────────────────
function ThinkingBubble({ text }) {
  return (
    <div style={{display:'flex',alignItems:'flex-start',gap:'10px'}}>
      <div style={{width:'28px',height:'28px',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,background:'linear-gradient(135deg, #7c3aed, #9333ea)'}}>
        <Bot style={{width:'13px',height:'13px',color:'white'}} />
      </div>
      <div style={{background:'#1f2937',border:'1px solid rgba(75,85,99,0.5)',borderRadius:'4px 16px 16px 16px',padding:'10px 14px',fontSize:'13px',color:'#9ca3af',display:'flex',alignItems:'center',gap:'8px'}}>
        <Loader2 style={{width:'13px',height:'13px',color:'#8b5cf6',animation:'spin 1s linear infinite'}} />
        <span>{text || 'Thinking...'}</span>
      </div>
    </div>
  );
}

// ─── Plan card ────────────────────────────────────────────────────────────────
function PlanCard({ actions, expanded, onToggle }) {
  return (
    <div style={{background:'rgba(31,41,55,0.8)',border:'1px solid rgba(139,92,246,0.3)',borderRadius:'12px',overflow:'hidden'}}>
      <button
        onClick={onToggle}
        style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',background:'transparent',border:'none',cursor:'pointer',color:'#c4b5fd',fontSize:'13px'}}
      >
        <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
          <Zap style={{width:'13px',height:'13px'}} />
          <span style={{fontWeight:500}}>{actions.length} action{actions.length !== 1 ? 's' : ''} planned</span>
        </div>
        {expanded ? <ChevronUp style={{width:'14px',height:'14px',color:'#6b7280'}} /> : <ChevronDown style={{width:'14px',height:'14px',color:'#6b7280'}} />}
      </button>
      {expanded && (
        <div style={{borderTop:'1px solid rgba(75,85,99,0.5)',padding:'8px 14px',display:'flex',flexDirection:'column',gap:'6px'}}>
          {actions.map((a, i) => {
            const Icon = ACTION_ICONS[a.type] || ACTION_ICONS.default;
            return (
              <div key={i} style={{display:'flex',alignItems:'flex-start',gap:'8px',fontSize:'12px',color:'#d1d5db'}}>
                <Icon style={{width:'13px',height:'13px',marginTop:'2px',color:'#8b5cf6',flexShrink:0}} />
                <span>{a.description || (a.type || '').replace(/_/g, ' ')}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Progress bar ─────────────────────────────────────────────────────────────
function ProgressBar({ completed, total }) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
      <div style={{display:'flex',justifyContent:'space-between',fontSize:'11px',color:'#6b7280'}}>
        <span>Applying changes</span>
        <span>{completed}/{total}</span>
      </div>
      <div style={{height:'4px',background:'#374151',borderRadius:'9999px',overflow:'hidden'}}>
        <div style={{height:'100%',background:'linear-gradient(90deg, #7c3aed, #9333ea)',borderRadius:'9999px',width:`${pct}%`,transition:'width 0.3s ease'}} />
      </div>
    </div>
  );
}

// ─── Result card ──────────────────────────────────────────────────────────────
function ResultCard({ applied, onUndo }) {
  const count = applied?.length || 0;
  return (
    <div style={{background:'rgba(16,185,129,0.1)',border:'1px solid rgba(16,185,129,0.3)',borderRadius:'12px',padding:'10px 14px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
      <div style={{display:'flex',alignItems:'center',gap:'8px',color:'#34d399',fontSize:'13px'}}>
        <CheckCircle2 style={{width:'14px',height:'14px'}} />
        <span>{count} change{count !== 1 ? 's' : ''} applied successfully</span>
      </div>
      {onUndo && (
        <button onClick={onUndo} style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'11px',color:'#6b7280',background:'transparent',border:'none',cursor:'pointer'}}>
          <RotateCcw style={{width:'11px',height:'11px'}} />
          Undo
        </button>
      )}
    </div>
  );
}

// ─── Error card ───────────────────────────────────────────────────────────────
function ErrorCard({ message: msg }) {
  return (
    <div style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:'12px',padding:'10px 14px',display:'flex',alignItems:'center',gap:'8px',color:'#f87171',fontSize:'13px'}}>
      <XCircle style={{width:'14px',height:'14px',flexShrink:0}} />
      <span>{msg}</span>
    </div>
  );
}

// ─── Failure + recovery card ─────────────────────────────────────────────────
function FailureRecoveryCard({ insight, expanded, onToggle }) {
  if (!insight) return null;

  const toolLabel = (insight.tool || 'unknown_action').replace(/_/g, ' ');
  const reason = insight.reason || 'Action failed.';
  const recoveryAction = insight.recoveryAction || 'Waiting for next recovery step...';

  return (
    <div
      data-testid="sitebrain-recovery-panel"
      style={{
        background:'rgba(245,158,11,0.08)',
        border:'1px solid rgba(245,158,11,0.25)',
        borderRadius:'12px',
        overflow:'hidden',
      }}
    >
      <div style={{padding:'10px 12px',display:'flex',flexDirection:'column',gap:'8px'}}>
        <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
          <AlertCircle style={{width:'14px',height:'14px',color:'#f59e0b',flexShrink:0}} />
          <span data-testid="sitebrain-recovery-panel-title" style={{fontSize:'12px',fontWeight:600,color:'#fbbf24'}}>Latest failed action</span>
          <span data-testid="sitebrain-recovery-action-name" style={{fontSize:'12px',color:'#fef3c7',marginLeft:'auto'}}>{toolLabel}</span>
        </div>

        <div style={{display:'grid',gap:'6px'}}>
          <div>
            <p style={{fontSize:'10px',color:'#92400e',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.05em',margin:0}}>Failure reason</p>
            <p data-testid="sitebrain-recovery-failure-reason" style={{fontSize:'12px',color:'#f3f4f6',margin:'2px 0 0',lineHeight:1.45}}>{reason}</p>
          </div>
          <div>
            <p style={{fontSize:'10px',color:'#92400e',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.05em',margin:0}}>Recovery step</p>
            <p data-testid="sitebrain-recovery-next-step" style={{fontSize:'12px',color:'#fde68a',margin:'2px 0 0',lineHeight:1.45}}>{recoveryAction}</p>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={onToggle}
        data-testid="sitebrain-recovery-details-toggle"
        style={{
          width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'8px',
          padding:'10px 12px',background:'rgba(120,53,15,0.18)',border:'none',cursor:'pointer',
          color:'#fde68a',fontSize:'11px',fontWeight:600,
        }}
      >
        <span>{expanded ? 'Hide recovery details' : 'Show recovery details'}</span>
        {expanded ? <ChevronUp style={{width:'13px',height:'13px'}} /> : <ChevronDown style={{width:'13px',height:'13px'}} />}
      </button>

      {expanded && (
        <div data-testid="sitebrain-recovery-details-panel" style={{padding:'10px 12px',borderTop:'1px solid rgba(245,158,11,0.18)',display:'grid',gap:'8px'}}>
          {insight.stepId && (
            <div>
              <p style={{fontSize:'10px',color:'#92400e',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.05em',margin:0}}>Step</p>
              <p data-testid="sitebrain-recovery-step-id" style={{fontSize:'12px',color:'#d1d5db',margin:'2px 0 0'}}>{insight.stepId}</p>
            </div>
          )}
          <div>
            <p style={{fontSize:'10px',color:'#92400e',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.05em',margin:0}}>Recovery reasoning</p>
            <p data-testid="sitebrain-recovery-reasoning" style={{fontSize:'12px',color:'#d1d5db',margin:'2px 0 0',lineHeight:1.45}}>
              {insight.recoveryThought || 'SiteBrain has not explained its next move yet.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Clarification card ───────────────────────────────────────────────────────
function ClarificationCard({ questions, onAnswer }) {
  return (
    <div style={{background:'rgba(245,158,11,0.1)',border:'1px solid rgba(245,158,11,0.3)',borderRadius:'12px',padding:'12px 14px',display:'flex',flexDirection:'column',gap:'8px'}}>
      <div style={{display:'flex',alignItems:'center',gap:'8px',color:'#fbbf24',fontSize:'13px',fontWeight:500}}>
        <AlertCircle style={{width:'14px',height:'14px'}} />
        <span>A few questions to help me do this right:</span>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
        {questions.map((q, i) => (
          <button
            key={i}
            onClick={() => onAnswer(q)}
            style={{display:'flex',alignItems:'flex-start',gap:'8px',fontSize:'12px',color:'#d1d5db',background:'transparent',border:'none',cursor:'pointer',textAlign:'left',padding:'4px 0'}}
          >
            <ArrowRight style={{width:'11px',height:'11px',marginTop:'2px',color:'#fbbf24',flexShrink:0}} />
            <span style={{textDecoration:'underline',textDecorationStyle:'dotted'}}>{q}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Quick Actions Panel ──────────────────────────────────────────────────────
function QuickActionsPanel({ onSend }) {
  const [activeCategory, setActiveCategory] = useState(0);

  return (
    <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
      {/* Category tabs */}
      <div style={{display:'flex',gap:'4px',overflowX:'auto',paddingBottom:'2px'}}>
        {QUICK_ACTION_CATEGORIES.map((cat, i) => {
          const Icon = cat.icon;
          return (
            <button
              key={i}
              onClick={() => setActiveCategory(i)}
              style={{
                display:'flex',alignItems:'center',gap:'5px',padding:'5px 10px',
                borderRadius:'20px',border:'none',cursor:'pointer',whiteSpace:'nowrap',
                fontSize:'11px',fontWeight:500,transition:'all 0.15s',flexShrink:0,
                background: activeCategory === i ? cat.color : '#1f2937',
                color: activeCategory === i ? 'white' : '#9ca3af',
              }}
            >
              <Icon style={{width:'11px',height:'11px'}} />
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Actions for active category */}
      <div style={{display:'flex',flexDirection:'column',gap:'5px'}}>
        {QUICK_ACTION_CATEGORIES[activeCategory].actions.map((qa, i) => {
          const Icon = qa.icon;
          return (
            <button
              key={i}
              onClick={() => onSend(qa.prompt)}
              style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 12px',borderRadius:'8px',background:'#1f2937',border:'1px solid rgba(75,85,99,0.4)',cursor:'pointer',textAlign:'left',transition:'all 0.15s'}}
              onMouseEnter={e => { e.currentTarget.style.background='#374151'; e.currentTarget.style.borderColor=`rgba(139,92,246,0.4)`; }}
              onMouseLeave={e => { e.currentTarget.style.background='#1f2937'; e.currentTarget.style.borderColor='rgba(75,85,99,0.4)'; }}
            >
              <Icon style={{width:'13px',height:'13px',color:QUICK_ACTION_CATEGORIES[activeCategory].color,flexShrink:0}} />
              <span style={{fontSize:'12px',color:'#d1d5db',flex:1}}>{qa.label}</span>
              <ArrowRight style={{width:'11px',height:'11px',color:'#4b5563'}} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Capabilities overview ────────────────────────────────────────────────────
function CapabilitiesOverview() {
  return (
    <div style={{background:'rgba(31,41,55,0.5)',border:'1px solid rgba(75,85,99,0.3)',borderRadius:'10px',padding:'10px 12px'}}>
      <p style={{fontSize:'11px',color:'#6b7280',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.05em',margin:'0 0 8px'}}>What I can do</p>
      <div style={{display:'flex',flexWrap:'wrap',gap:'6px'}}>
        {CAPABILITIES.map((cap, i) => {
          const Icon = cap.icon;
          return (
            <div key={i} style={{display:'flex',alignItems:'center',gap:'5px',padding:'3px 8px',borderRadius:'20px',background:'rgba(139,92,246,0.1)',border:'1px solid rgba(139,92,246,0.2)'}}>
              <Icon style={{width:'10px',height:'10px',color:'#8b5cf6'}} />
              <span style={{fontSize:'11px',color:'#c4b5fd'}}>{cap.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Verification Card ────────────────────────────────────────────────────────
function VerificationCard({ verified, confidence, evidence, gaps }) {
  return (
    <div style={{
      background: verified ? 'rgba(16,185,129,0.08)' : 'rgba(245,158,11,0.08)',
      border: `1px solid ${verified ? 'rgba(16,185,129,0.25)' : 'rgba(245,158,11,0.25)'}`,
      borderRadius: '10px', padding: '10px 12px',
    }}>
      <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'6px'}}>
        {verified
          ? <CheckCircle2 style={{width:'14px',height:'14px',color:'#10b981'}} />
          : <AlertCircle style={{width:'14px',height:'14px',color:'#f59e0b'}} />
        }
        <span style={{fontSize:'12px',fontWeight:600,color: verified ? '#10b981' : '#f59e0b'}}>
          {verified ? `Verified (${Math.round(confidence * 100)}% confidence)` : `Verification Failed (${Math.round(confidence * 100)}%)`}
        </span>
      </div>
      {evidence && (
        <p style={{fontSize:'11px',color:'#9ca3af',margin:'0 0 4px',lineHeight:1.4}}>{evidence}</p>
      )}
      {gaps && gaps.length > 0 && (
        <div style={{marginTop:'6px'}}>
          <p style={{fontSize:'10px',color:'#6b7280',fontWeight:600,margin:'0 0 3px',textTransform:'uppercase',letterSpacing:'0.05em'}}>Gaps</p>
          {gaps.map((g, i) => (
            <div key={i} style={{display:'flex',alignItems:'flex-start',gap:'5px',marginTop:'2px'}}>
              <XCircle style={{width:'10px',height:'10px',color:'#f59e0b',flexShrink:0,marginTop:'2px'}} />
              <span style={{fontSize:'11px',color:'#d1d5db'}}>{g}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Suggestion Chips ─────────────────────────────────────────────────────────
function SuggestionChips({ suggestions, onSend }) {
  if (!suggestions || suggestions.length === 0) return null;
  return (
    <div style={{
      background: 'rgba(139,92,246,0.06)',
      border: '1px solid rgba(139,92,246,0.2)',
      borderRadius: '10px', padding: '10px 12px',
    }}>
      <div style={{display:'flex',alignItems:'center',gap:'6px',marginBottom:'8px'}}>
        <Zap style={{width:'12px',height:'12px',color:'#a78bfa'}} />
        <span style={{fontSize:'11px',fontWeight:600,color:'#a78bfa',textTransform:'uppercase',letterSpacing:'0.05em'}}>What's next?</span>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
        {suggestions.map((s, i) => (
          <button
            key={i}
            onClick={() => onSend(s.prompt || s.title)}
            style={{
              display:'flex',flexDirection:'column',gap:'2px',padding:'8px 10px',
              borderRadius:'8px',background:'rgba(139,92,246,0.1)',
              border:'1px solid rgba(139,92,246,0.2)',cursor:'pointer',textAlign:'left',
              transition:'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background='rgba(139,92,246,0.18)'; e.currentTarget.style.borderColor='rgba(139,92,246,0.4)'; }}
            onMouseLeave={e => { e.currentTarget.style.background='rgba(139,92,246,0.1)'; e.currentTarget.style.borderColor='rgba(139,92,246,0.2)'; }}
          >
            <span style={{fontSize:'12px',fontWeight:600,color:'#c4b5fd'}}>{s.title}</span>
            {s.description && <span style={{fontSize:'11px',color:'#8b8ba0'}}>{s.description}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Main SiteBrainPanel ──────────────────────────────────────────────────────
export default function TemplateEditorSiteBrainPanel({ websiteId, onApply, currentPageId: initialPageId, currentPageSlug: initialPageSlug }) {
  // currentPageId and currentPageSlug are now passed from parent
  const [activePageId, setActivePageId] = useState(initialPageId || null);
  const [activePageSlug, setActivePageSlug] = useState(initialPageSlug || 'home');
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      content: "Hi! I'm SiteBrain — your **autonomous** AI website agent.\n\nI work in an intelligent loop: **Observe → Think → Act → Verify** — just like a real developer.\n\nTell me your goal and I'll iteratively build it, verifying each step works before moving on.\n\n*Examples: Add a pricing page, create a booking form, change the hero text, set up an e-commerce store, add a contact form with backend storage...*",
    }
  ]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [thinkingText, setThinkingText] = useState('');
  const [currentPlan, setCurrentPlan] = useState(null);
  const [planExpanded, setPlanExpanded] = useState(true);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [lastChangeset, setLastChangeset] = useState(null);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [conversationHistory, setConversationHistory] = useState([]);
  // Autonomous plan state (Phase 2)
  const [autonomousPlan, setAutonomousPlan] = useState(null);
  const [activeStepId, setActiveStepId] = useState(null);
  const [completedStepIds, setCompletedStepIds] = useState([]);
  const [failedStepIds, setFailedStepIds] = useState([]);
  const [failureInsight, setFailureInsight] = useState(null);
  const [showFailureDetails, setShowFailureDetails] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const pendingRecoveryRef = useRef(false);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, isStreaming, scrollToBottom]);

  const addMessage = useCallback((msg) => {
    setMessages(prev => [...prev, { id: Date.now() + Math.random(), ...msg }]);
  }, []);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isStreaming) return;

    const userText = text.trim();
    setInput('');
    setShowQuickActions(false);
    setCurrentPlan(null);
    setProgress({ completed: 0, total: 0 });

    addMessage({ role: 'user', content: userText });
    const newHistory = [...conversationHistory, { role: 'user', content: userText }];
    setConversationHistory(newHistory);

    setIsStreaming(true);
    setThinkingText('Analyzing your request...');
    setAutonomousPlan(null);
    setActiveStepId(null);
    setCompletedStepIds([]);
    setFailedStepIds([]);
    setFailureInsight(null);
    setShowFailureDetails(false);
    pendingRecoveryRef.current = false;

    const token = localStorage.getItem('token');

    try {
      // Pass the current page info to the API
          const response = await fetch(`${API_URL}/api/ai/sitebrain/agent-stream`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              website_id: websiteId,
              message: userText,
              conversation_history: conversationHistory,
              current_page_id: activePageSlug || initialPageSlug || 'home',
            }),
          });

      if (!response.ok) {
        const errText = await response.text();
        console.error('SiteBrain API error:', response.status, errText);
        let errData = {};
        try {
          errData = errText ? JSON.parse(errText) : {};
        } catch {
          errData = {};
        }
        throw new Error(errData?.detail || `Server error: ${response.status} - ${errText.substring(0, 200)}`);
      }

      // Check content-type to determine if it's SSE stream or regular response
      const contentType = response.headers.get('content-type') || '';
      const isStream = contentType.includes('text/event-stream');
      
      if (!isStream) {
        // Non-streaming response (could be JSON error or simple response)
        const jsonData = await response.json().catch(() => null);
        console.log('Non-streaming response:', jsonData);
        addMessage({ role: 'assistant', content: JSON.stringify(jsonData, null, 2) });
        setIsStreaming(false);
        return;
      }

      // SSE stream - read it
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assistantReply = '';
      let planActions = null;
      let totalActions = 0;
      let changesetId = null;
      
      // Track current event type from SSE "event:" line
      let currentEventType = '';
      
      // Timeout to prevent stuck in streaming
      let lastEventTime = Date.now();
      const STREAM_TIMEOUT = 60000; // 60 seconds

      while (true) {
        const { done, value } = await reader.read();
        
        // Check for timeout
        if (Date.now() - lastEventTime > STREAM_TIMEOUT) {
          console.log('Stream timeout - no events for 60 seconds');
          setThinkingText('Request timed out. Please try again.');
          setIsStreaming(false);
          break;
        }
        
        if (done) {
          // Stream ended normally
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          // Handle SSE "event:" line to set current event type
          if (line.startsWith('event: ')) {
            currentEventType = line.slice(7).trim();
            lastEventTime = Date.now();
            continue;
          }
          
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;

          let evt;
          try { evt = JSON.parse(jsonStr); } catch { continue; }

          // Update last event time
          lastEventTime = Date.now();

          // Support both old-style {type, content} and new SSE-style event lines
          // Use currentEventType if set, otherwise fall back to evt.type
          const evtType = currentEventType || evt.type || evt.event || '';
          const evtData = evt.data || evt;
          
          // Reset event type after using it
          currentEventType = '';

          // Debug unknown events
          if (!['agent.text', 'agent.step', 'agent.complete', 'agent.error', 'tool.start', 'tool.result', 
                'thinking', 'reply', 'session.status', 'agent.thought', 'tool.call', 'tool.result',
                'agent.finish', 'plan.created', 'epic.start', 'task.start', 'task.complete', 'task.failed',
                'page.created', 'page.updated', 'site.published', 'stream.end', 'done'].includes(evtType)) {
            console.log('Unknown event type:', evtType, evtData);
          }

          switch (evtType) {
            // ── New Dyad-style events ───────────────────────────────────────
            case 'agent.text': {
              const text = evtData.content || '';
              if (text) {
                assistantReply += text;
                // Show as thinking text
                setThinkingText(text.substring(0, 100));
              }
              break;
            }

            case 'tool.start': {
              const toolName = evtData.tool || 'unknown';
              setThinkingText(`Running ${toolName}...`);
              break;
            }

            // ── Legacy event types (backward compat) ──────────────────────
            case 'thinking':
              setThinkingText(evtData.content || evt.content || 'Thinking...');
              break;

            case 'reply':
              assistantReply = evtData.content || evt.content || '';
              setThinkingText('');
              break;

            case 'plan':
              planActions = evtData.actions || evt.actions || [];
              totalActions = evtData.count || evt.count || planActions.length;
              setCurrentPlan(planActions);
              setProgress({ completed: 0, total: totalActions });
              setThinkingText('Applying changes...');
              break;

            case 'progress': {
              const current = evtData.current ?? evtData.index ?? evt.index;
              const total = evtData.total ?? totalActions;
              if (total > 0) totalActions = total;
              setProgress({ completed: current != null ? current : 0, total: totalActions });
              if (evtData.message) setThinkingText(evtData.message);
              break;
            }

            case 'done':
              changesetId = evtData.changeset_id || evt.changeset_id;
              setThinkingText('');
              setIsStreaming(false);
              if (assistantReply) {
                addMessage({ role: 'assistant', content: assistantReply });
                setConversationHistory(prev => [...prev, { role: 'assistant', content: assistantReply }]);
              }
              if (planActions && planActions.length > 0) {
                const applied = evtData.applied || evt.applied || [];
                if (applied.length > 0) {
                  setLastChangeset(changesetId);
                  addMessage({ role: 'result', applied, changesetId });
                  if (onApply) onApply();
                }
              }
              if (evtData.questions && evtData.questions.length > 0) {
                addMessage({ role: 'clarification', questions: evtData.questions });
              }
              break;

            // ── SiteBrain SDK v4 orchestrator events ──────────────────────

            case 'agent.thought': {
              const thought = evtData.thought || evtData.message || evtData.content || '';
              const agent = evtData.agent || '';
              const label = agent ? `${agent}: ${thought}` : thought;
              if (pendingRecoveryRef.current && thought && !/^Thinking\.\.\./.test(thought)) {
                setFailureInsight(prev => prev ? ({ ...prev, recoveryThought: thought }) : prev);
              }
              setThinkingText(label ? label.slice(0, 120) : 'Thinking...');
              break;
            }

            case 'agent.message': {
              const agentMsg = evtData.message || evtData.content || '';
              if (agentMsg) {
                assistantReply = agentMsg;
                addMessage({ role: 'assistant', content: agentMsg });
                setConversationHistory(prev => [...prev, { role: 'assistant', content: agentMsg }]);
              }
              break;
            }

            case 'project.stage': {
              const desc = evtData.description || evtData.stage || '';
              setThinkingText(desc || 'Processing...');
              break;
            }

            case 'spec.complete': {
              const spec = evtData.spec || {};
              setThinkingText(spec.clarified_description ? `Spec ready: ${spec.clarified_description.slice(0, 80)}...` : 'Specification complete');
              break;
            }

            case 'plan.created': {
              const planPayload = evtData.plan || {};
              if (evtData.label === 'autonomous' && planPayload.steps) {
                setAutonomousPlan(planPayload);
                setThinkingText(`Planning ${planPayload.steps.length} steps...`);
              } else if (planPayload.actions) {
                planActions = planPayload.actions;
                totalActions = planPayload.actions.length;
                setCurrentPlan(planActions);
                setProgress({ completed: 0, total: totalActions });
                setThinkingText('Applying changes...');
              } else if (evtData.epics) {
                const allTasks = evtData.epics.flatMap(e =>
                  (e.tasks || []).map(t => ({ type: t.task_type || 'task', description: t.description }))
                );
                planActions = allTasks;
                totalActions = evtData.total_tasks || allTasks.length;
                setCurrentPlan(allTasks);
                setProgress({ completed: 0, total: totalActions });
                setThinkingText(`Plan ready: ${totalActions} tasks across ${evtData.epics.length} phases`);
              }
              break;
            }

            case 'epic.start': {
              const epicDesc = evtData.description || '';
              if (epicDesc) setThinkingText(`Phase: ${epicDesc}`);
              break;
            }

            case 'task.start': {
              const taskDesc = evtData.description || '';
              const taskTotal = evtData.total || totalActions;
              if (taskTotal > 0) totalActions = taskTotal;
              setThinkingText(taskDesc ? `Working on: ${taskDesc}` : 'Executing task...');
              break;
            }

            case 'task.complete': {
              const doneDesc = evtData.description || '';
              if (doneDesc) setThinkingText(`Done: ${doneDesc}`);
              if (onApply) onApply();
              break;
            }

            case 'task.failed': {
              const failDesc = evtData.description || 'Task failed';
              const failErr = evtData.error || '';
              addMessage({ role: 'error', content: failErr ? `${failDesc}: ${failErr}` : failDesc });
              break;
            }

            case 'page.created':
            case 'page.updated': {
              const pageName = evtData.page_name || evtData.page_slug || '';
              setThinkingText(evtType === 'page.created' ? `Created page: ${pageName}` : `Updated page: ${pageName}`);
              if (onApply) onApply();
              break;
            }

            case 'site.published': {
              setThinkingText('Site published!');
              if (onApply) onApply();
              break;
            }

            case 'tool.call': {
              const toolName = evtData.tool || evtData.step_id || 'tool';
              const toolArgs = evtData.args || {};
              const toolLabel = toolName.replace(/_/g, ' ');
              if (pendingRecoveryRef.current) {
                setFailureInsight(prev => prev ? ({ ...prev, recoveryAction: toolLabel }) : prev);
                pendingRecoveryRef.current = false;
              }
              setThinkingText(`Using ${toolLabel}...`);
              if (evtData.step_id) setActiveStepId(evtData.step_id);
              setCurrentPlan(prev => {
                const existing = prev || [];
                const alreadyThere = existing.some(a => a.id === evtData.step_id);
                if (alreadyThere) return existing;
                return [...existing, { id: evtData.step_id, type: toolName, description: toolArgs.description || toolArgs.instruction || toolLabel }];
              });
              totalActions = (currentPlan?.length || 0) + 1;
              setProgress(p => ({ completed: p.completed, total: p.total + 1 }));
              break;
            }

            case 'tool.result': {
              const toolOk = evtData.success !== false;
              if (!toolOk) {
                const failureReason = evtData?.result?.error || evtData?.result?.summary || evtData.error || 'Action failed.';
                setFailureInsight({
                  tool: evtData.tool || 'unknown_action',
                  stepId: evtData.step_id || null,
                  reason: failureReason,
                  recoveryThought: '',
                  recoveryAction: '',
                });
                setShowFailureDetails(false);
                pendingRecoveryRef.current = true;
              }
              if (evtData.step_id) {
                if (toolOk) {
                  setCompletedStepIds(prev => [...prev, evtData.step_id]);
                  setProgress(p => ({ completed: p.completed + 1, total: p.total }));
                } else {
                  setFailedStepIds(prev => [...prev, evtData.step_id]);
                }
                setActiveStepId(null);
              }
              if (toolOk && evtData.tool !== 'think') {
                  // Mark as having pending changes, but don't call onApply yet
                  // onApply will be called only when agent.finish is received
                  setLastChangeset('pending-' + Date.now());
                }
              break;
            }

            case 'tool.result': {
              const toolName = evtData.tool || 'unknown';
              const result = evtData.result || '';
              setThinkingText(`[${toolName}] ${result.substring(0, 100)}...`);
              assistantReply += `\n[${toolName}] ${result}`;
              // Trigger refresh
              if (onApply) onApply();
              break;
            }

            case 'agent.step': {
              const stepNum = evtData.step || 1;
              setThinkingText(`Step ${stepNum}...`);
              break;
            }

            case 'agent.complete': {
              const summary = evtData.summary || 'Completed';
              assistantReply = summary;
              addMessage({ role: 'assistant', content: summary });
              setThinkingText('');
              setIsStreaming(false);
              if (onApply) onApply();
              break;
            }

            case 'agent.error': {
              const errMsg = evtData.message || 'An error occurred';
              addMessage({ role: 'error', content: errMsg });
              setThinkingText('');
              setIsStreaming(false);
              break;
            }

            case 'agent.complete':
            case 'complete': {
              const summary = evtData.summary || 'Completed';
              assistantReply = summary;
              addMessage({ role: 'assistant', content: summary });
              setThinkingText('');
              setIsStreaming(false);
              if (onApply) onApply();
              break;
            }

            case 'agent.finish': {
              const summary = evtData.summary || evtData.content || 'Task completed.';
              const finishSuggestions = evtData.suggestions || [];
              assistantReply = summary;
              addMessage({ role: 'assistant', content: summary });
              setConversationHistory(prev => [...prev, { role: 'assistant', content: summary }]);
              if (finishSuggestions.length > 0) {
                addMessage({ role: 'suggestions', suggestions: finishSuggestions });
              }
              setThinkingText('');
              setIsStreaming(false);
              setActiveStepId(null);
              const completedCount = completedStepIds.length + 1;
              console.log('SiteBrain agent.finish - completedCount:', completedCount, 'onApply:', typeof onApply);
              if (completedCount > 0) {
                setLastChangeset('sdk-' + Date.now());
                addMessage({ role: 'result', applied: Array(completedCount).fill({ type: 'change' }), changesetId: null });
              }
              if (onApply) {
                console.log('SiteBrain calling onApply callback');
                onApply();
              }
              break;
            }

            case 'agent.verify': {
              const verified = evtData.verified;
              const confidence = evtData.confidence || 0;
              const evidence = evtData.evidence || '';
              const gaps = evtData.gaps || [];
              if (verified) {
                addMessage({ role: 'verification', verified: true, confidence, evidence });
                setThinkingText('');
              } else {
                addMessage({ role: 'verification', verified: false, confidence, evidence, gaps });
                setThinkingText('Verification failed — continuing work...');
              }
              break;
            }

            case 'agent.suggestions': {
              const agentSuggestions = evtData.suggestions || [];
              if (agentSuggestions.length > 0) {
                addMessage({ role: 'suggestions', suggestions: agentSuggestions });
              }
              break;
            }

            case 'agent.error': {
              const errMsg = evtData.error || evtData.content || 'An error occurred.';
              setThinkingText('');
              setIsStreaming(false);
              addMessage({ role: 'error', content: errMsg });
              break;
            }

            case 'token.usage':
              break;

            case 'session.status': {
              const st = evtData.status || '';
              if (st === 'complete') {
                const completeSummary = evtData.summary || '';
                if (completeSummary && !assistantReply) {
                  assistantReply = completeSummary;
                  addMessage({ role: 'assistant', content: completeSummary });
                  setConversationHistory(prev => [...prev, { role: 'assistant', content: completeSummary }]);
                }
                setThinkingText('');
                setIsStreaming(false);
                setActiveStepId(null);
                if (totalActions > 0) {
                  setLastChangeset('session-' + Date.now());
                  addMessage({ role: 'result', applied: Array(totalActions).fill({ type: 'change' }), changesetId: null });
                }
                if (onApply) onApply();
              } else if (st === 'error') {
                const sessionErr = evtData.message || evtData.error || 'An error occurred.';
                setThinkingText('');
                setIsStreaming(false);
                addMessage({ role: 'error', content: sessionErr });
              } else if (st === 'waiting_for_clarification') {
                setThinkingText('');
                setIsStreaming(false);
              } else if (st === 'executing_autonomous_plan') {
                setThinkingText('Executing plan...');
              } else if (st === 'verifying') {
                setThinkingText('🔍 Running independent verification...');
              }
              break;
            }

            case 'assistant.message': {
              const finalText = evtData.finalText || evtData.content || '';
              if (finalText) {
                assistantReply = finalText;
                addMessage({ role: 'assistant', content: finalText });
                setConversationHistory(prev => [...prev, { role: 'assistant', content: finalText }]);
              }
              setThinkingText('');
              setIsStreaming(false);
              break;
            }

            case 'clarification': {
              const questions = evtData.questions || [];
              if (questions.length > 0) {
                addMessage({ role: 'clarification', questions });
              }
              setThinkingText('');
              setIsStreaming(false);
              break;
            }

            case 'error':
              setThinkingText('');
              setIsStreaming(false);
              addMessage({ role: 'error', content: evtData.content || evt.content || 'An error occurred.' });
              break;

            default:
              break;
          }
        }
      }
    } catch (err) {
      setThinkingText('');
      setIsStreaming(false);
      if (err.name !== 'AbortError') {
        addMessage({ role: 'error', content: err.message || 'Connection failed. Please try again.' });
      }
    }
  }, [isStreaming, websiteId, conversationHistory, addMessage, onApply]);

  const handleUndo = useCallback(async () => {
    if (!lastChangeset) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/ai/page-agent/undo-last`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ website_id: websiteId }),
      });
      if (res.ok) {
        addMessage({ role: 'system', content: 'Last change undone successfully.' });
        setLastChangeset(null);
        if (onApply) onApply();
      }
    } catch {
      addMessage({ role: 'error', content: 'Failed to undo last change.' });
    }
  }, [lastChangeset, websiteId, addMessage, onApply]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const clearChat = () => {
    setMessages([{
      id: 'welcome-new',
      role: 'assistant',
      content: "Chat cleared. How can I help you with your website?",
    }]);
    setConversationHistory([]);
    setCurrentPlan(null);
    setLastChangeset(null);
    setShowQuickActions(true);
    setFailureInsight(null);
    setShowFailureDetails(false);
    pendingRecoveryRef.current = false;
  };

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',background:'#111827',color:'white',overflow:'hidden'}}>
      {/* ── Header ── */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px',borderBottom:'1px solid rgba(75,85,99,0.4)',background:'rgba(17,24,39,0.9)',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
          <div style={{width:'32px',height:'32px',borderRadius:'8px',background:'linear-gradient(135deg, #7c3aed, #9333ea)',display:'flex',alignItems:'center',justifyContent:'center',position:'relative'}}>
            <Sparkles style={{width:'15px',height:'15px',color:'white'}} />
            <div style={{position:'absolute',bottom:'-2px',right:'-2px',width:'10px',height:'10px',borderRadius:'50%',background:'#10b981',border:'2px solid #111827'}} />
          </div>
          <div>
            <div style={{fontSize:'13px',fontWeight:600,color:'white',lineHeight:1}}>SiteBrain Autonomous Agent</div>
            <div style={{fontSize:'11px',color:'#10b981',marginTop:'2px',display:'flex',alignItems:'center',gap:'4px'}}>
              <span style={{width:'5px',height:'5px',borderRadius:'50%',background:'#10b981',display:'inline-block'}} />
              OODA Loop · Observe → Think → Act → Verify
            </div>
          </div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'4px'}}>
          {/* ── Current Page Indicator ── */}
          <div style={{display:'flex',alignItems:'center',gap:'6px',padding:'4px 8px',background:'rgba(124,58,237,0.2)',borderRadius:'6px',fontSize:'11px',color:'#a78bfa',marginRight:'4px'}}>
            <FileText style={{width:'12px',height:'12px'}} />
            <span>{activePageSlug || 'home'}</span>
          </div>
          {lastChangeset && (
            <button data-testid="sitebrain-undo-last-change-button" onClick={handleUndo} title="Undo last change" style={{padding:'6px',borderRadius:'6px',background:'transparent',border:'none',cursor:'pointer',color:'#6b7280'}}>
              <RotateCcw style={{width:'13px',height:'13px'}} />
            </button>
          )}
          <button data-testid="sitebrain-clear-chat-button" onClick={clearChat} title="Clear chat" style={{padding:'6px',borderRadius:'6px',background:'transparent',border:'none',cursor:'pointer',color:'#6b7280'}}>
            <RefreshCw style={{width:'13px',height:'13px'}} />
          </button>
        </div>
      </div>

      {/* ── Messages ── */}
      <div style={{flex:1,overflowY:'auto',padding:'16px',display:'flex',flexDirection:'column',gap:'14px',minHeight:0}}>
        {messages.map((msg) => {
          if (msg.role === 'user' || msg.role === 'assistant' || msg.role === 'system') {
            return <MessageBubble key={msg.id} message={msg} />;
          }
          if (msg.role === 'error') return <ErrorCard key={msg.id} message={msg.content} />;
          if (msg.role === 'result') return <ResultCard key={msg.id} applied={msg.applied} onUndo={msg.changesetId ? handleUndo : null} />;
          if (msg.role === 'clarification') return <ClarificationCard key={msg.id} questions={msg.questions} onAnswer={(q) => sendMessage(q)} />;
          if (msg.role === 'verification') return <VerificationCard key={msg.id} verified={msg.verified} confidence={msg.confidence} evidence={msg.evidence} gaps={msg.gaps} />;
          if (msg.role === 'suggestions') return <SuggestionChips key={msg.id} suggestions={msg.suggestions} onSend={sendMessage} />;
          return null;
        })}

        {/* Streaming state */}
        {isStreaming && (
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {thinkingText && <ThinkingBubble text={thinkingText} />}
            {/* Autonomous plan steps (Phase 2) */}
            {autonomousPlan && autonomousPlan.steps && (
              <AutonomousStepCard
                steps={autonomousPlan.steps}
                currentStepId={activeStepId}
                completedSteps={completedStepIds}
                failedSteps={failedStepIds}
              />
            )}
            {/* Legacy plan card */}
            {!autonomousPlan && currentPlan && currentPlan.length > 0 && (
              <PlanCard actions={currentPlan} expanded={planExpanded} onToggle={() => setPlanExpanded(v => !v)} />
            )}
            {progress.total > 0 && (
              <ProgressBar completed={progress.completed} total={progress.total} />
            )}
          </div>
        )}
        {/* Show completed autonomous plan after streaming ends */}
        {!isStreaming && autonomousPlan && autonomousPlan.steps && completedStepIds.length > 0 && (
          <AutonomousStepCard
            steps={autonomousPlan.steps}
            currentStepId={null}
            completedSteps={completedStepIds}
            failedSteps={failedStepIds}
          />
        )}

        {failureInsight && (
          <FailureRecoveryCard
            insight={failureInsight}
            expanded={showFailureDetails}
            onToggle={() => setShowFailureDetails(prev => !prev)}
          />
        )}

        {/* Quick actions and capabilities */}
        {showQuickActions && !isStreaming && messages.length <= 1 && (
          <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
            <CapabilitiesOverview />
            <div>
              <p style={{fontSize:'11px',color:'#4b5563',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.05em',margin:'0 0 8px'}}>Quick actions</p>
              <QuickActionsPanel onSend={sendMessage} />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ── Input area ── */}
      <div style={{flexShrink:0,padding:'12px 16px',borderTop:'1px solid rgba(75,85,99,0.4)',background:'rgba(17,24,39,0.9)'}}>
        <div style={{display:'flex',alignItems:'flex-end',gap:'8px'}}>
          <textarea
            data-testid="sitebrain-chat-input"
            ref={textareaRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={handleKeyDown}
            placeholder="Ask me to change anything on your website..."
            rows={1}
            disabled={isStreaming}
            style={{
              flex:1,background:'#1f2937',border:'1px solid #374151',borderRadius:'10px',
              padding:'10px 14px',fontSize:'13px',color:'white',resize:'none',
              outline:'none',minHeight:'40px',maxHeight:'120px',lineHeight:'1.4',
              fontFamily:'inherit',transition:'border-color 0.15s',
              opacity: isStreaming ? 0.5 : 1,
            }}
            onFocus={e => e.target.style.borderColor='#7c3aed'}
            onBlur={e => e.target.style.borderColor='#374151'}
          />
          <button
            data-testid="sitebrain-send-button"
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || isStreaming}
            style={{
              width:'40px',height:'40px',borderRadius:'10px',border:'none',cursor:'pointer',
              display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,
              background: (!input.trim() || isStreaming) ? '#374151' : '#7c3aed',
              transition:'background 0.15s',
            }}
          >
            {isStreaming
              ? <Loader2 style={{width:'15px',height:'15px',color:'white',animation:'spin 1s linear infinite'}} />
              : <Send style={{width:'15px',height:'15px',color:'white'}} />
            }
          </button>
        </div>
        <p style={{fontSize:'11px',color:'#374151',marginTop:'6px',textAlign:'center',margin:'6px 0 0'}}>
          Enter to send · Shift+Enter for new line
        </p>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .group:hover .copy-btn { opacity: 1 !important; }
      `}</style>
    </div>
  );
}
